import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { parseRenderOptions } from './utils';
import { FieldWrapper } from './FieldWrapper';
import { List } from 'immutable';
import Dropzone from 'react-dropzone';
import classNames from 'classnames';
import { Icon } from '../Icon';

const FileComponent = ({ renderOptions, ...props }) => {
  const { onChange, multiple } = props;
  // State for keeping track of links to the files
  const [fileRenders, setFileRenders] = useState([]);

  // Convert the value to an array of files
  const files = useMemo(
    () =>
      List.isList(props.value)
        ? props.value
        : Array.isArray(props.value)
          ? List(props.value)
          : List(),
    [props.value],
  );

  // When the files change, update the fileLinks
  useEffect(() => {
    // Create an object URL for any uploaded Files, and parse the link for
    // existing files to remove the space slug from the start
    const fileRenderersData = files
      .map(file => {
        return file instanceof File
          ? {
              url: URL.createObjectURL(file),
              isFile: true,
              name: file.name || 'Unnamed File',
            }
          : {
              url: file.get('link') && file.get('link').replace(/^\/[^/]*/, ''),
              isFile: false,
              name: file.get('name') || 'Unnamed File',
            };
      })
      .toJS();
    setFileRenders(fileRenderersData);

    // Revoke object URLs
    return () => {
      fileRenderersData.forEach(fileLink => {
        if (fileLink && fileLink.isFile) {
          URL.revokeObjectURL(fileLink.url);
        }
      });
    };
  }, [files]);

  const onRemove = useCallback(
    index => () => {
      onChange(files.remove(index));
    },
    [files, onChange],
  );

  const onAdd = useCallback(
    acceptedFiles => {
      onChange(
        multiple
          ? files.push(...acceptedFiles)
          : List([acceptedFiles[0]]).filter(Boolean),
      );
    },
    [files, multiple, onChange],
  );

  return (
    <FieldWrapper
      {...props}
      renderOptions={renderOptions}
      fieldset={props.multiple}
    >
      {fileRenders.map((file, i) => (
        <div className="field-floating" key={`${file.name}-${i}`}>
          <div
            className={classNames('form-control form-control--file disabled', {
              'form-control-sm': renderOptions.size === 'sm',
              'has-btn': props.enabled,
            })}
          >
            <a href={file.url || undefined} download={file.name}>
              {file.name}
            </a>
          </div>
          {props.enabled && (
            <button
              type="button"
              className={classNames('btn btn-link-primary btn-floating', {
                'btn-sm': renderOptions.size === 'sm',
              })}
              onClick={onRemove(i)}
              aria-label="Remove File"
              title="Remove File"
            >
              <Icon name="x" md />
            </button>
          )}
        </div>
      ))}
      {(props.multiple || files.size === 0) && (
        <Dropzone
          onDropAccepted={onAdd}
          multiple={props.multiple}
          disabled={!props.enabled}
          accept={renderOptions.custom.accept}
        >
          {({ getRootProps, getInputProps, isDragReject, isDragAccept }) => (
            <div
              {...getRootProps()}
              className={classNames('form-control form-control--file-upload', {
                disabled: !props.enabled,
                'form-control-sm': renderOptions.size === 'sm',
                'is-accepted': isDragAccept,
                'is-invalid': isDragReject,
              })}
            >
              <input
                {...getInputProps()}
                id={props.id}
                name={props.name}
                onBlur={props.onBlur}
                onFocus={props.onFocus}
                form={props.form}
              />
              <span>
                {renderOptions.placeholder ||
                  `Drag and drop ${
                    props.multiple ? 'files' : 'a file'
                  } or click to browse`}
              </span>
            </div>
          )}
        </Dropzone>
      )}
    </FieldWrapper>
  );
};

export const FileField = props => {
  const renderOptions = parseRenderOptions(props, {
    isFloating: false,
  });
  return <FileComponent renderOptions={renderOptions} {...props} />;
};

export const FileMultiField = props => {
  const renderOptions = parseRenderOptions(props, {
    isFloating: false,
  });
  return (
    <FileComponent renderOptions={renderOptions} {...props} multiple={true} />
  );
};
